import {
  Sorting,
  ColumnWidthInfo,
  ColumnExtension,
  GridFilter,
} from "../../common/store/types";
import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { SignedEdition, SortOrder } from "../../../api/graphql";
import type { DataGridColumn } from "../../../common/controls/lists/DataGrid";
import {
  isOperatorAvailable,
  type FilterOperator,
} from "../../../common/services/mrtFilterService";
import { signedEditionDndaWorkflowStateDisplayId } from "../../signedEditionDndaWorkflowState/store/SignedEditionDndaWorkflowStateCommonStore";
import { signedEditionSadaicWorkflowStateDisplayId } from "../../signedEditionSadaicWorkflowState/store/SignedEditionSadaicWorkflowStateCommonStore";
import { musicalWorkDisplayId } from "../../musicalWork/store/MusicalWorkCommonStore";
import { associatedDocumentSignedEditionDisplayId } from "../../associatedDocumentSignedEdition/store/AssociatedDocumentSignedEditionCommonStore";
import { signedEditionBlockchainCertDisplayId } from "../../signedEditionBlockchainCert/store/SignedEditionBlockchainCertCommonStore";
import {
  calendarDateToDate,
  diaDeCalendario,
} from "../../../common/calendarDate";

export type ColumnInfo = Record<
  string,
  {
    sortingEnabled: boolean;
    width: number;
    isHidden: boolean;
  }
>;

const VERSION = "1.0.0";

/**
 * La celda de Excel de un date: la medianoche UTC de su día, que ExcelJS escribe como
 * ese día en cualquier zona (cuenta los días en UTC). Sin valor, o sin un día, vacía.
 */
const excelCalendarDate = (value: string | null | undefined): Date | "" => {
  const day = diaDeCalendario(value);
  return day === null ? "" : calendarDateToDate(day);
};

export type RowClickViewMode = "route" | "drawer";

export type SignedEditionListProfile = {
  VERSION: string;
  columnOrder: string[];
  columnsInfo: ColumnInfo;
  sorting: Sorting[];
  filtering: GridFilter[];
  showColumnFilters?: boolean;
  viewMode?: RowClickViewMode;
};

export class SignedEditionGridDataStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  errorDescription: string = "";
  errorDialogOpened: boolean = false;
  filtering: GridFilter[] = [];

  // Estado de UI del DataGrid (sincronizado con ListToolbox)
  showColumnFilters: boolean = false;
  isFullScreen: boolean = false;
  columnMenuOpen: boolean = false;
  viewMode: RowClickViewMode = "route";

  get ProfileKey(): string[] {
    return ["SignedEdition", "list"];
  }

  columnsInfo: ColumnInfo = {};
  initialColumnsInfo: ColumnInfo = {};

  sortExpression: Record<
    string,
    (direction: SortOrder, lang: LangStore) => any
  > = {
    "data_.name": (direction: SortOrder, _lang: LangStore) => {
      return { name: direction };
    },
    "data_.editionNumber": (direction: SortOrder, _lang: LangStore) => {
      return { editionNumber: direction };
    },
    dndaState: (direction: SortOrder, _lang: LangStore) => {
      return { dndaState: { [_lang.fieldByLang("name")]: direction } };
    },
    sadaicState: (direction: SortOrder, _lang: LangStore) => {
      return { sadaicState: { [_lang.fieldByLang("name")]: direction } };
    },
    works: (direction: SortOrder, _lang: LangStore) => {
      return { works: { _count: direction } };
    },
    dndaDocuments: (direction: SortOrder, _lang: LangStore) => {
      return { dndaDocuments: { _count: direction } };
    },
    blockchainCertificates: (direction: SortOrder, _lang: LangStore) => {
      return { blockchainCertificates: { _count: direction } };
    },
  };

  initialColumnOrder = [
    "data_.name",
    "data_.editionNumber",
    "publicationDate",
    "dndaState",
    "sadaicState",
    "works",
    "dndaExpedienteNumber",
    "dndaDocuments",
    "ejemplarPdf",
    "dndaComprobantePdf",
    "blockchainCertificates",
    "dndaUploadLink",
    "dndaUploadKeyReceivedAt",
    "dndaUploadKeyExpiresAt",
    "dndaComprobanteReceivedAt",
    "dndaLastComprobantePollAt",
    "dndaLastOperationalError",
    "sadaicDeliveredAt",
    "sadaicLastOperationalError",
    "createdAt_",
    "lastUpdatedAt_",
    "id",
  ];
  columnOrder = [
    "data_.name",
    "data_.editionNumber",
    "publicationDate",
    "dndaState",
    "sadaicState",
    "works",
    "dndaExpedienteNumber",
    "dndaDocuments",
    "ejemplarPdf",
    "dndaComprobantePdf",
    "blockchainCertificates",
    "dndaUploadLink",
    "dndaUploadKeyReceivedAt",
    "dndaUploadKeyExpiresAt",
    "dndaComprobanteReceivedAt",
    "dndaLastComprobantePollAt",
    "dndaLastOperationalError",
    "sadaicDeliveredAt",
    "sadaicLastOperationalError",
    "createdAt_",
    "lastUpdatedAt_",
    "id",
  ];

  _sorting: Sorting[];

  // Restricciones de rol por columna (columnas que requieren roles específicos)
  readonly columnRoleRestrictions: Record<string, string[]> = {
    dndaLastOperationalError: ["SysAdmin"],
    sadaicLastOperationalError: ["SysAdmin"],
  };

  // Columna de grilla → campo real por el que filtrar. Las pseudo-columnas del
  // snapshot (`data_.X`) no existen en SQL: filtran por la columna promovida
  // con .searchable.
  readonly columnFilterAlias: Record<string, string> = {
    "data_.name": "name",
    "data_.editionNumber": "editionNumber",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.columnsInfo = {
      "data_.name": {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      "data_.editionNumber": {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      publicationDate: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      dndaState: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      sadaicState: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      works: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaExpedienteNumber: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaDocuments: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      ejemplarPdf: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaComprobantePdf: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      blockchainCertificates: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaUploadLink: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaUploadKeyReceivedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaUploadKeyExpiresAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaComprobanteReceivedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaLastComprobantePollAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaLastOperationalError: {
        sortingEnabled: true,
        width: 300,
        isHidden: !["SysAdmin"].some((role) =>
          this.rootStore.authStore.hasRole(role)
        ),
      },
      sadaicDeliveredAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      sadaicLastOperationalError: {
        sortingEnabled: true,
        width: 300,
        isHidden: !["SysAdmin"].some((role) =>
          this.rootStore.authStore.hasRole(role)
        ),
      },
      createdAt_: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      lastUpdatedAt_: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      id: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
    };
    this.initialColumnsInfo = {
      "data_.name": {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      "data_.editionNumber": {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      publicationDate: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      dndaState: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      sadaicState: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      works: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaExpedienteNumber: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaDocuments: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      ejemplarPdf: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaComprobantePdf: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      blockchainCertificates: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaUploadLink: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaUploadKeyReceivedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaUploadKeyExpiresAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaComprobanteReceivedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaLastComprobantePollAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dndaLastOperationalError: {
        sortingEnabled: true,
        width: 300,
        isHidden: !["SysAdmin"].some((role) =>
          this.rootStore.authStore.hasRole(role)
        ),
      },
      sadaicDeliveredAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      sadaicLastOperationalError: {
        sortingEnabled: true,
        width: 300,
        isHidden: !["SysAdmin"].some((role) =>
          this.rootStore.authStore.hasRole(role)
        ),
      },
      createdAt_: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      lastUpdatedAt_: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      id: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
    };
    this._sorting = [{ columnName: "createdAt_", direction: SortOrder.Desc }];
    makeAutoObservable(this);
  }

  // Verifica si el usuario actual tiene permiso para ver una columna
  isColumnAllowedForUser(columnName: string): boolean {
    const requiredRoles = this.columnRoleRestrictions[columnName];
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // No hay restricción
    }
    return requiredRoles.some((role) => this.rootStore.authStore.hasRole(role));
  }

  get subFilters(): Record<
    string,
    { subFields: string[]; multi: boolean } | undefined
  > {
    return {
      dndaState: {
        subFields: [this.langStore.fieldByLang("name")],
        multi: false,
      },
      sadaicState: {
        subFields: [this.langStore.fieldByLang("name")],
        multi: false,
      },
      works: {
        subFields: ["title"],
        multi: true,
      },
      dndaDocuments: {
        subFields: ["name"],
        multi: true,
      },
      blockchainCertificates: {
        subFields: ["name"],
        multi: true,
      },
    };
  }

  get searchFields(): string[] {
    return [];
  }

  get sorting() {
    return this._sorting;
  }

  set sorting(sorting: Sorting[]) {
    this._sorting = sorting;
  }

  get mrtColumnVisibility(): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    for (const [key, info] of Object.entries(this.columnsInfo)) {
      result[key] = !info.isHidden;
    }
    return result;
  }

  setMrtColumnVisibility(visibility: Record<string, boolean>) {
    for (const [key, isVisible] of Object.entries(visibility)) {
      if (this.columnsInfo[key]) {
        this.columnsInfo[key].isHidden = !isVisible;
        // Si se oculta la columna, borrar su filtro (UX: lo que ves es lo que filtras)
        if (!isVisible) {
          this.filtering = this.filtering.filter((f) => f.columnName !== key);
        }
      }
    }
  }

  showAllColumns() {
    for (const key of Object.keys(this.columnsInfo)) {
      this.columnsInfo[key].isHidden = false;
    }
  }

  hideAllColumns() {
    for (const key of Object.keys(this.columnsInfo)) {
      this.columnsInfo[key].isHidden = true;
    }
  }

  // Métodos para estado de UI del DataGrid
  setShowColumnFilters(value: boolean) {
    this.showColumnFilters = value;
  }

  toggleShowColumnFilters() {
    this.showColumnFilters = !this.showColumnFilters;
  }

  setIsFullScreen(value: boolean) {
    this.isFullScreen = value;
  }

  toggleIsFullScreen() {
    this.isFullScreen = !this.isFullScreen;
  }

  setColumnMenuOpen(value: boolean) {
    this.columnMenuOpen = value;
  }

  openColumnMenu() {
    this.columnMenuOpen = true;
  }

  closeColumnMenu() {
    this.columnMenuOpen = false;
  }

  setViewMode(mode: RowClickViewMode) {
    this.viewMode = mode;
  }

  toggleViewMode() {
    this.viewMode = this.viewMode === "drawer" ? "route" : "drawer";
  }

  get mrtColumnSizing(): Record<string, number> {
    const result: Record<string, number> = {};
    for (const [key, info] of Object.entries(this.columnsInfo)) {
      result[key] = info.width;
    }
    return result;
  }

  setMrtColumnSizing(sizing: Record<string, number>) {
    for (const [key, width] of Object.entries(sizing)) {
      if (this.columnsInfo[key]) {
        this.columnsInfo[key].width = width;
      }
    }
  }

  get columnWidths(): ColumnWidthInfo[] {
    return Object.keys(this.columnsInfo).map((key) => {
      return { columnName: key, width: this.columnsInfo[key].width };
    });
  }

  setColumnWidths(columnWidths: ColumnWidthInfo[]) {
    for (const cw of columnWidths) {
      this.columnsInfo[cw.columnName].width = Number(cw.width);
    }
  }

  get columnExtensions(): ColumnExtension[] {
    return Object.keys(this.columnsInfo).map((key) => {
      return {
        columnName: key,
        sortingEnabled: this.columnsInfo[key].sortingEnabled,
      };
    });
  }

  setColumnExtensions(columnExtensions: ColumnExtension[]) {
    for (const ce of columnExtensions) {
      this.columnsInfo[ce.columnName].sortingEnabled = ce.sortingEnabled;
    }
  }

  setColumnOrder(columnOrder: string[]) {
    this.columnOrder = columnOrder;
  }

  resetColumnOrder() {
    this.columnOrder = [...this.initialColumnOrder];
    // Restaurar visibilidad y ancho inicial de columnas
    for (const key of Object.keys(this.columnsInfo)) {
      if (this.initialColumnsInfo[key]) {
        this.columnsInfo[key].isHidden = this.initialColumnsInfo[key].isHidden;
        this.columnsInfo[key].width = this.initialColumnsInfo[key].width;
      }
    }
  }

  setSorting(sorting: Sorting[]) {
    this.sorting = sorting;
  }

  get columnTypes() {
    return {
      "data_.name": "String",
      "data_.editionNumber": "Int",
      publicationDate: "Date",
      dndaState: "__Reference",
      sadaicState: "__Reference",
      works: "__Reference",
      dndaExpedienteNumber: "String",
      dndaDocuments: "__SubEntity",
      ejemplarPdf: "File",
      dndaComprobantePdf: "File",
      blockchainCertificates: "__SubEntity",
      dndaUploadLink: "String",
      dndaUploadKeyReceivedAt: "DateTime",
      dndaUploadKeyExpiresAt: "DateTime",
      dndaComprobanteReceivedAt: "DateTime",
      dndaLastComprobantePollAt: "DateTime",
      dndaLastOperationalError: "String",
      sadaicDeliveredAt: "DateTime",
      sadaicLastOperationalError: "String",
      createdAt_: "DateTime",
      lastUpdatedAt_: "DateTime",
      data_: "Json",
      name: "String",
      editionNumber: "Int",
      id: "String",
    } as Record<string, string>;
  }

  get filteringColumns() {
    return [
      { columnName: "data_.name", filteringEnabled: true },
      { columnName: "data_.editionNumber", filteringEnabled: true },
      { columnName: "publicationDate", filteringEnabled: true },
      { columnName: "dndaState", filteringEnabled: true },
      { columnName: "sadaicState", filteringEnabled: true },
      { columnName: "works", filteringEnabled: true },
      { columnName: "dndaExpedienteNumber", filteringEnabled: true },
      { columnName: "dndaDocuments", filteringEnabled: true },
      { columnName: "ejemplarPdf", filteringEnabled: true },
      { columnName: "dndaComprobantePdf", filteringEnabled: true },
      { columnName: "blockchainCertificates", filteringEnabled: true },
      { columnName: "dndaUploadLink", filteringEnabled: true },
      { columnName: "dndaUploadKeyReceivedAt", filteringEnabled: true },
      { columnName: "dndaUploadKeyExpiresAt", filteringEnabled: true },
      { columnName: "dndaComprobanteReceivedAt", filteringEnabled: true },
      { columnName: "dndaLastComprobantePollAt", filteringEnabled: true },
      { columnName: "dndaLastOperationalError", filteringEnabled: true },
      { columnName: "sadaicDeliveredAt", filteringEnabled: true },
      { columnName: "sadaicLastOperationalError", filteringEnabled: true },
      { columnName: "createdAt_", filteringEnabled: true },
      { columnName: "lastUpdatedAt_", filteringEnabled: true },
      { columnName: "data_", filteringEnabled: false },
      { columnName: "name", filteringEnabled: true },
      { columnName: "editionNumber", filteringEnabled: true },
      { columnName: "id", filteringEnabled: true },
    ];
  }

  serverColumn(columnName: string) {
    return columnName;
  }

  setSelectedFilters(filters: GridFilter[]) {
    this.filtering = filters;
  }

  get serverFilters() {
    // Solo aplicar filtros si están visibles (showColumnFilters = true)
    return this.showColumnFilters ? this.filtering : [];
  }

  serverSearch(value: string) {
    return this.searchFields.map((x) => {
      return {
        columnName: x,
        operation: "contains",
        value: value,
      };
    });
  }

  get defaultFilters() {
    return this.filtering;
  }

  get numberColumns() {
    return ["data_.editionNumber", "editionNumber"];
  }

  get booleanColumns() {
    return [];
  }

  get dateColumns() {
    return [
      "publicationDate",
      "dndaUploadKeyReceivedAt",
      "dndaUploadKeyExpiresAt",
      "dndaComprobanteReceivedAt",
      "dndaLastComprobantePollAt",
      "sadaicDeliveredAt",
      "createdAt_",
      "lastUpdatedAt_",
    ];
  }

  get nullableDateColumns() {
    return [
      "publicationDate",
      "dndaUploadKeyReceivedAt",
      "dndaUploadKeyExpiresAt",
      "dndaComprobanteReceivedAt",
      "dndaLastComprobantePollAt",
      "sadaicDeliveredAt",
      "lastUpdatedAt_",
    ] as string[];
  }

  get xlsColumns(): { header: string; key: string; width: number }[] {
    const columns = [];
    for (const column of this.columnOrder) {
      if (
        !this.columnsInfo[column].isHidden &&
        (column.length < 2 || column.slice(0, 2) !== "__")
      ) {
        const width = (this.columnsInfo[column].width / 10) * 1.33;
        columns.push({
          header: this.columnTitles[column],
          key: column,
          width,
        });
      }
    }
    return columns;
  }

  get OrderBy() {
    let orderBy = [];
    for (const s of this.sorting) {
      const sortExpression = this.sortExpression[s.columnName];
      if (sortExpression) {
        const ob = sortExpression(
          s.direction === "desc" ? SortOrder.Desc : SortOrder.Asc,
          this.langStore
        );
        orderBy.push(ob);
      } else {
        orderBy.push({
          [s.columnName]:
            s.direction === "desc" ? SortOrder.Desc : SortOrder.Asc,
        });
      }
    }
    return orderBy;
  }

  get columnTitles(): Record<string, string> {
    return {
      dndaState: `${this.langStore.SignedEditionDndaStateLabel}`,
      sadaicState: `${this.langStore.SignedEditionSadaicStateLabel}`,
      works: `${this.langStore.SignedEditionWorksLabel}`,
      publicationDate: `${this.langStore.SignedEditionPublicationDateLabel}`,
      dndaExpedienteNumber: `${this.langStore.SignedEditionDndaExpedienteNumberLabel}`,
      dndaDocuments: `${this.langStore.SignedEditionDndaDocumentsLabel}`,
      ejemplarPdf: `${this.langStore.SignedEditionEjemplarPdfLabel}`,
      dndaComprobantePdf: `${this.langStore.SignedEditionDndaComprobantePdfLabel}`,
      blockchainCertificates: `${this.langStore.SignedEditionBlockchainCertificatesLabel}`,
      dndaUploadLink: `${this.langStore.SignedEditionDndaUploadLinkLabel}`,
      dndaUploadKeyReceivedAt: `${this.langStore.SignedEditionDndaUploadKeyReceivedAtLabel}`,
      dndaUploadKeyExpiresAt: `${this.langStore.SignedEditionDndaUploadKeyExpiresAtLabel}`,
      dndaComprobanteReceivedAt: `${this.langStore.SignedEditionDndaComprobanteReceivedAtLabel}`,
      dndaLastComprobantePollAt: `${this.langStore.SignedEditionDndaLastComprobantePollAtLabel}`,
      dndaLastOperationalError: `${this.langStore.SignedEditionDndaLastOperationalErrorLabel}`,
      sadaicDeliveredAt: `${this.langStore.SignedEditionSadaicDeliveredAtLabel}`,
      sadaicLastOperationalError: `${this.langStore.SignedEditionSadaicLastOperationalErrorLabel}`,
      createdAt_: `${this.langStore.SignedEditionCreatedAt_Label}`,
      lastUpdatedAt_: `${this.langStore.SignedEditionLastUpdatedAt_Label}`,
      data_: `${this.langStore.SignedEditionData_Label}`,
      name: `${this.langStore.SignedEditionNameLabel}`,
      editionNumber: `${this.langStore.SignedEditionEditionNumberLabel}`,
      id: `Id`,
      "data_.name": `${this.langStore.EditionNameLabel}`,
      "data_.editionNumber": `${this.langStore.EditionEditionNumberLabel}`,
    };
  }

  // Un date (día de calendario) filtra en su propia columna, sin zona ni hora
  // ('calendarDate'); un dateTime sigue siendo 'date'.
  private getMrtColumnType(
    type: string
  ): "string" | "number" | "date" | "calendarDate" | "boolean" {
    if (type === "Int" || type === "Decimal") return "number";
    if (type === "Date") return "calendarDate";
    if (type === "DateTime") return "date";
    if (type === "Boolean") return "boolean";
    return "string";
  }

  get dataGridColumns(): DataGridColumn<Record<string, any>>[] {
    // Usar initialColumnOrder (orden fijo) para definir columnas
    // MRT maneja reorden visual con prop columnOrder separada
    // Esto permite que "Reset Order" funcione correctamente
    const nullableDateColumns = this.nullableDateColumns;
    return this.initialColumnOrder
      .filter(
        (name) =>
          (name.length < 2 || name.slice(0, 2) !== "__") &&
          this.isColumnAllowedForUser(name)
      )
      .map((name) => {
        const columnType = this.columnTypes[name];
        const filterCol = this.filteringColumns.find(
          (f: any) => f.columnName === name
        );
        const baseColumn = {
          header: this.columnTitles[name] || name,
          size: this.columnsInfo[name]?.width || 150,
          columnType: this.getMrtColumnType(columnType),
          nullable: nullableDateColumns.includes(name),
          filterEnabled: filterCol?.filteringEnabled ?? true,
          sortingEnabled: this.columnsInfo[name]?.sortingEnabled ?? true,
        };

        // JSON fields need accessorFn to stringify the value
        if (columnType === "Json") {
          return {
            ...baseColumn,
            id: name,
            accessorFn: (row: any) => {
              const val = row[name];
              if (val == null) return "";
              const s = JSON.stringify(val);
              return s.length > 100 ? s.substring(0, 100) + "..." : s;
            },
          };
        }

        return {
          ...baseColumn,
          accessorKey: name,
        };
      });
  }

  // Lista de columnas para el ColumnMenu (usado en ListToolbox)
  get columnMenuItems(): { id: string; header: string }[] {
    return this.initialColumnOrder
      .filter(
        (name) =>
          (name.length < 2 || name.slice(0, 2) !== "__") &&
          this.isColumnAllowedForUser(name)
      )
      .map((name) => ({
        id: name,
        header: this.columnTitles[name] || name,
      }));
  }

  signedEditionFormat(e: SignedEdition) {
    return {
      "data_.name": (e as any).data_ ? (e as any).data_?.name ?? "" : "",
      "data_.editionNumber": (e as any).data_
        ? (e as any).data_?.editionNumber ?? ""
        : "",
      publicationDate: excelCalendarDate(e.publicationDate),
      dndaState: e.dndaState
        ? signedEditionDndaWorkflowStateDisplayId(e.dndaState, this.langStore)
        : "",
      sadaicState: e.sadaicState
        ? signedEditionSadaicWorkflowStateDisplayId(
            e.sadaicState,
            this.langStore
          )
        : "",
      works: e.works
        ? e.works.map((x: any) => musicalWorkDisplayId(x, this.langStore))
        : [],
      dndaExpedienteNumber: e.dndaExpedienteNumber,
      dndaDocuments: e.dndaDocuments
        ? e.dndaDocuments.map((x: any) =>
            associatedDocumentSignedEditionDisplayId(x, this.langStore)
          )
        : [],
      ejemplarPdf: e.ejemplarPdf,
      dndaComprobantePdf: e.dndaComprobantePdf,
      blockchainCertificates: e.blockchainCertificates
        ? e.blockchainCertificates.map((x: any) =>
            signedEditionBlockchainCertDisplayId(x, this.langStore)
          )
        : [],
      dndaUploadLink: e.dndaUploadLink,
      dndaUploadKeyReceivedAt: e.dndaUploadKeyReceivedAt
        ? new Date(e.dndaUploadKeyReceivedAt)
        : "",
      dndaUploadKeyExpiresAt: e.dndaUploadKeyExpiresAt
        ? new Date(e.dndaUploadKeyExpiresAt)
        : "",
      dndaComprobanteReceivedAt: e.dndaComprobanteReceivedAt
        ? new Date(e.dndaComprobanteReceivedAt)
        : "",
      dndaLastComprobantePollAt: e.dndaLastComprobantePollAt
        ? new Date(e.dndaLastComprobantePollAt)
        : "",
      dndaLastOperationalError: e.dndaLastOperationalError,
      sadaicDeliveredAt: e.sadaicDeliveredAt
        ? new Date(e.sadaicDeliveredAt)
        : "",
      sadaicLastOperationalError: e.sadaicLastOperationalError,
      createdAt_: new Date(e.createdAt_),
      lastUpdatedAt_: e.lastUpdatedAt_ ? new Date(e.lastUpdatedAt_) : "",
      data_: e.data_
        ? (() => {
            const s = JSON.stringify(e.data_);
            return s.length > 100 ? s.substring(0, 100) + "..." : s;
          })()
        : "",
      name: e.name,
      editionNumber: e.editionNumber,
      id: e.id,
    };
  }

  get gridState(): any {
    // Solo guardar filtros si están habilitados Y tienen valores
    const hasActiveFilters =
      this.showColumnFilters && this.filtering.length > 0;
    return {
      columnOrder: this.columnOrder,
      columnsInfo: this.columnsInfo,
      sorting: this.sorting,
      filtering: hasActiveFilters ? this.filtering : [],
      // showColumnFilters solo true si hay filtros activos
      showColumnFilters: hasActiveFilters,
      viewMode: this.viewMode,
      VERSION,
    };
  }

  setGridState(gridState: SignedEditionListProfile) {
    // Nunca debe fallar la carga del profile - el cliente manda, el profile sugiere
    try {
      if (!gridState || typeof gridState !== "object") return;
      // No rechazar por VERSION diferente - validamos cada campo individualmente
      // Esto permite migrar profiles de versiones anteriores

      const validColumns = new Set(Object.keys(this.columnsInfo));

      if (gridState.columnsInfo && typeof gridState.columnsInfo === "object") {
        const mergedColumnsInfo: ColumnInfo = {};

        for (const [key, defaultValue] of Object.entries(this.columnsInfo)) {
          mergedColumnsInfo[key] = { ...defaultValue };
        }

        for (const [key, savedValue] of Object.entries(gridState.columnsInfo)) {
          if (
            validColumns.has(key) &&
            typeof savedValue === "object" &&
            savedValue !== null
          ) {
            const defaultValue = mergedColumnsInfo[key];

            mergedColumnsInfo[key] = {
              sortingEnabled:
                typeof savedValue.sortingEnabled === "boolean"
                  ? savedValue.sortingEnabled
                  : defaultValue.sortingEnabled,

              width:
                typeof savedValue.width === "number" &&
                savedValue.width > 0 &&
                isFinite(savedValue.width)
                  ? savedValue.width
                  : defaultValue.width,

              isHidden:
                typeof savedValue.isHidden === "boolean"
                  ? savedValue.isHidden
                  : defaultValue.isHidden,
            };
          }
        }

        this.columnsInfo = mergedColumnsInfo;
      }

      if (Array.isArray(gridState.columnOrder)) {
        const validOrderColumns = gridState.columnOrder.filter(
          (col) => typeof col === "string" && validColumns.has(col)
        );
        const newColumns = Array.from(validColumns).filter(
          (col) => !gridState.columnOrder.includes(col)
        );
        this.columnOrder = [...validOrderColumns, ...newColumns];
      }

      if (Array.isArray(gridState.sorting)) {
        this.sorting = gridState.sorting.filter(
          (sort) =>
            typeof sort === "object" &&
            sort !== null &&
            typeof sort.columnName === "string" &&
            validColumns.has(sort.columnName) &&
            (sort.direction === "asc" || sort.direction === "desc")
        );
        if (this.sorting.length === 0) {
          this.sorting = [
            { columnName: "createdAt_", direction: SortOrder.Desc },
          ];
        }
      }

      if (Array.isArray(gridState.filtering)) {
        const nullableDateColumns = this.nullableDateColumns;
        this.filtering = gridState.filtering.filter(
          (filter) =>
            typeof filter === "object" &&
            filter !== null &&
            typeof filter.columnName === "string" &&
            validColumns.has(filter.columnName) &&
            typeof filter.operation === "string" &&
            filter.hasOwnProperty("value") &&
            // "Vacío" y "No vacío" solo en una fecha que puede ser nula. Un perfil
            // guardado cuando la columna era otra cosa no puede dejar un filtro que
            // el server rechaza en cada carga (el DataGrid también lo descarta, pero
            // el perfil puede llegar acá antes).
            isOperatorAvailable(
              this.getMrtColumnType(this.columnTypes[filter.columnName]),
              filter.operation as FilterOperator,
              nullableDateColumns.includes(filter.columnName)
            )
        );
      }

      // Si hay filtros guardados, mostrar los filtros automáticamente
      // Si el profile guardó showColumnFilters, usar ese valor
      if (typeof gridState.showColumnFilters === "boolean") {
        this.showColumnFilters = gridState.showColumnFilters;
      } else if (this.filtering.length > 0) {
        // Compatibilidad: si hay filtros pero no se guardó showColumnFilters, activarlos
        this.showColumnFilters = true;
      }

      if (gridState.viewMode === "route" || gridState.viewMode === "drawer") {
        this.viewMode = gridState.viewMode;
      }
    } catch (error) {
      // Silenciar errores de carga de profile - usar valores por defecto
      console.warn(
        "[GridDataStore] Error loading profile, using defaults:",
        error
      );
    }
  }
}
